import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.browser.localStorage

/**
 * Veřejná (bez přihlášení) stránka se soupiskami. Ukazuje kontakt na kapitána, takže je schovaná
 * za jednoduché statické heslo -- nejde o skutečné zabezpečení (data jsou stejně čitelná přes
 * veřejné REST API, RLS je na player/player_season_roster "for select using (true)"), jen o to,
 * aby stránku nenašla náhodou kdejaká vyhledávačová/robotí procházka. Odemčení se pamatuje
 * v localStorage, takže ho uživatel zadává jen jednou (dokud si prohlížeč nevymaže data stránky).
 */
private const val ROSTERS_PASSWORD = "18"
private const val ROSTERS_UNLOCK_KEY = "soupisky_unlocked"

@Composable
fun RostersScreen(season: SeasonDto?) {
    var unlocked by remember { mutableStateOf(localStorage.getItem(ROSTERS_UNLOCK_KEY) == "true") }
    var passwordInput by remember { mutableStateOf("") }
    var passwordError by remember { mutableStateOf<String?>(null) }

    if (!unlocked) {
        Div(attrs = { classes("card") }) {
            Text("Stránka je chráněná heslem.")
            Div {
                Input(type = InputType.Password) {
                    placeholder("Heslo")
                    value(passwordInput)
                    onInput { passwordInput = it.value }
                }
                Button(attrs = {
                    onClick {
                        if (passwordInput == ROSTERS_PASSWORD) {
                            localStorage.setItem(ROSTERS_UNLOCK_KEY, "true")
                            unlocked = true
                            passwordError = null
                        } else {
                            passwordError = "Nesprávné heslo."
                        }
                    }
                }) { Text("Odemknout") }
            }
            passwordError?.let { Div { Text(it) } }
        }
        return
    }

    var teams by remember { mutableStateOf<List<SeasonLeagueTeamDto>>(emptyList()) }
    var rosterByTeam by remember { mutableStateOf<Map<String, List<RosterEntryDto>>>(emptyMap()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(season?.id) {
        val currentSeason = season
        if (currentSeason == null) {
            loading = false
            return@LaunchedEffect
        }
        loading = true
        try {
            teams = Api.fetchAllTeamsForSeason(null, currentSeason.id)
                .distinctBy { it.teamId }
                .sortedBy { it.teamNameOverride ?: it.team?.name ?: "" }
            rosterByTeam = Api.fetchRosterForSeason(null, currentSeason.id).groupBy { it.teamId }
            error = null
        } catch (e: Throwable) {
            error = "Načtení soupisek selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    Div(attrs = { classes("card") }) {
        H2 { Text("Soupisky" + (season?.let { " — ${it.name}" } ?: "")) }
        Div(attrs = { classes("player-card-holder") }) {
            Text("Hráči zapsaní v této barvě jsou držiteli slevové karty pro trénink na drahách Bowlingu U Papírny.")
        }
        if (season == null) {
            Text("Právě není žádná aktivní sezóna.")
        } else {
            if (loading) Text("Načítám…")
            error?.let { Div { Text("Chyba: $it") } }

            Div(attrs = { classes("roster-tiles") }) {
                teams.forEach { team ->
                    val teamName = team.teamNameOverride ?: team.team?.name ?: team.teamId
                    val entries = rosterByTeam[team.teamId] ?: emptyList()
                    val captain = entries.find { it.captain }
                    val others = entries.filterNot { it.captain }

                    Div(attrs = { classes("card", "roster-tile") }) {
                        H3 { Text(teamName) }

                        Div(attrs = { classes("captain-block") }) {
                            if (captain != null) {
                                Div {
                                    B(attrs = { if (captain.player?.card == true) classes("player-card-holder") }) {
                                        Text(captain.player?.fullName ?: captain.player?.name ?: "—")
                                    }
                                    RegBadge(captain.player?.registered)
                                }
                                Div { Text(captain.player?.email ?: "—") }
                                Div { Text(captain.player?.phone ?: "—") }
                            } else {
                                Div(attrs = { classes("muted") }) { Text("Kapitán není nastavený") }
                            }
                        }

                        if (others.isEmpty()) {
                            Div(attrs = { classes("muted") }) { Text("Žádní další hráči na soupisce.") }
                        } else {
                            Ul {
                                others.sortedBy { it.player?.fullName ?: it.player?.name ?: "" }.forEach { entry ->
                                    val label = entry.player?.fullName ?: entry.player?.name ?: entry.playerId
                                    Li(attrs = { if (entry.player?.card == true) classes("player-card-holder") }) {
                                        Text(label)
                                        RegBadge(entry.player?.registered)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Malá ikonka „R" s tooltipem u registrovaných hráčů. */
@Composable
private fun RegBadge(registered: Boolean?) {
    if (registered == true) {
        Span(attrs = { classes("reg-badge"); attr("title", "Registrovaný") }) { Text("R") }
    }
}
